import { useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'

import type { FilterChangedEvent } from 'ag-grid-community'
import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement, ChangeEvent } from 'react'
import type { IPlacement } from '~/types/placements'

import getColumns from './columns'
import CreatePlacementModal from './CreatePlacementModal'

import ContentLayout from '~/components/ContentLayout'
import Layout from '~/components/Layouts/Layout'
import Section from '~/components/Section'
import ServerSideTableWrapper from '~/components/Tables/ServerSideTableWrapper'
import ViewTitle from '~/components/ViewTitle'
import { AUTHORIZATIONS } from '~/const'
import useDocumentTitle from '~/hooks/useDocumentTitle'
import { useTablePagination } from '~/hooks/useTablePagination'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import { useAppsListQuery } from '~/queries/applications'
import { placementsKeys, usePlacementsQuery } from '~/queries/placements'
import { debounce } from '~/utils/function'
import { displayToast } from '~/utils/toast'

import './styles.scss'

const ENTRIES_NAME = 'placements'
const SEARCH_FIELD = 'key'

const Placements = (): ReactElement => {
  /** Local state */

  const placementWriteAuthorized = useIsUserAuthorized([AUTHORIZATIONS.DYNAMIC_SCREENS.PLACEMENT.WRITE])

  const queryClient = useQueryClient()

  const gridRef = useRef<AgGridReact<IPlacement>>(null)

  const tableRef = useRef<HTMLDivElement>(null)

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  const paginationApi = useTablePagination(ENTRIES_NAME)

  const { page, search, setFilters, setPage, setSearch, size } = paginationApi

  /** Queries & Mutations */

  const { data: apps } = useAppsListQuery({})

  const columnDefs = getColumns(apps?.data || [])

  const { data: placementsData, isLoading: isPlacementsLoading } = usePlacementsQuery({
    key: search[SEARCH_FIELD] as string,
    page,
    size,
  })

  /** Effects */

  useDocumentTitle('DS Placements')

  /** Handlers */

  const handleSearchChange = debounce(
    (evt: ChangeEvent<HTMLInputElement>) => setSearch({ [SEARCH_FIELD]: evt.target.value }),
    500
  )

  const handleFilterChanged = (event: FilterChangedEvent) => {
    const { app_key } = event.api.getFilterModel()
    const filter = app_key?.values && app_key.values[0]
    setFilters({ appKey: filter })
    setPage(0)
  }

  const handleAddClick = () => setIsCreateModalOpen(true)

  const toggleCreateModal = (isOpen: boolean) => () => setIsCreateModalOpen(isOpen)

  const handleSuccess = () => {
    setIsCreateModalOpen(false)
    queryClient.invalidateQueries({ queryKey: placementsKeys.all })
    displayToast('Placement successfully created', 'success')
  }

  const handleFailure = () => {
    setIsCreateModalOpen(false)
    displayToast('Placement could not be created', 'error')
  }

  return (
    <Layout className="placements-view" withPadding>
      <ViewTitle className="layout-view-title layout-view-title">Placements</ViewTitle>
      <ContentLayout>
        <Section className="section">
          <ServerSideTableWrapper
            addButton
            addButtonDisabled={!placementWriteAuthorized}
            addButtonLabel="New placement"
            addButtonTooltip={!placementWriteAuthorized ? 'unauthorized' : undefined}
            className="placements-table with-min-height"
            columnDefs={columnDefs}
            currentPage={page}
            entriesName={ENTRIES_NAME}
            loading={isPlacementsLoading}
            onAddButtonClick={handleAddClick}
            onFilterChanged={handleFilterChanged}
            onSearchChange={handleSearchChange}
            paginationApi={paginationApi}
            ref={gridRef}
            rowData={placementsData?.data || []}
            searchField={SEARCH_FIELD}
            tableRef={tableRef}
            total={placementsData?.total || 0}
            withSearchBar
          />
        </Section>
      </ContentLayout>
      <CreatePlacementModal
        isOpen={isCreateModalOpen}
        onClose={toggleCreateModal(false)}
        onFailure={handleFailure}
        onSuccess={handleSuccess}
      />
    </Layout>
  )
}

export default Placements
